import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Tenant } from '@/types';
import { useAuth } from '@/hooks/useAuth';

interface TenantContextProps {
  tenant: Tenant | null;
  loading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextProps>({
  tenant: null,
  loading: true,
  error: null,
});

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { tenantId: authTenantId } = useAuth();

  useEffect(() => {
    async function resolveTenant() {
      setLoading(true);
      let resolvedTenant: Tenant | null = null;

      // 1. Prefer the slug from the URL (allows PLATFORM_ADMIN to act on any tenant)
      if (tenantSlug) {
        try {
          const q = query(collection(db, 'tenants'), where('slug', '==', tenantSlug), limit(1));
          const snap = await getDocs(q);
          if (!snap.empty) {
            resolvedTenant = { id: snap.docs[0].id, ...snap.docs[0].data() } as Tenant;
          }
        } catch (err) {
          // Slug query may fail if rules haven't been deployed yet; fall through to auth claim
          console.warn('[TenantContext] Slug query failed, falling back to auth claim:', err);
        }
      }

      // 2. Fallback: use the tenantId stored in auth claims
      if (!resolvedTenant && authTenantId && authTenantId !== 'platform') {
        try {
          const q = query(collection(db, 'tenants'), where('__name__', '==', authTenantId), limit(1));
          const snap = await getDocs(q);
          if (!snap.empty) {
            resolvedTenant = { id: snap.docs[0].id, ...snap.docs[0].data() } as Tenant;
          }
        } catch (err: any) {
          console.error('Error resolving tenant:', err);
          setError(err.message);
        }
      }

      setTenant(resolvedTenant);
      setLoading(false);
    }

    resolveTenant();
  }, [tenantSlug, authTenantId]);

  return (
    <TenantContext.Provider value={{ tenant, loading, error }}>
      {children}
    </TenantContext.Provider>
  );
}

export const useTenantContext = () => useContext(TenantContext);
